import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
  LogarithmicScale
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Download,
  Maximize2,
  Minimize2,
  Calendar,
  Filter,
  RefreshCw,
  Info,
  Eye,
  EyeOff,
  PieChart,
  BarChart3,
  LineChart,
  Target,
  Award,
  Zap,
  Sparkles
} from 'lucide-react';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
  LogarithmicScale
);

// ==========================================================
// 📊 PROFESSIONAL CHART - VERSÃO ULTRA ÉLITE
// 📱 100% RESPONSIVA - ✨ EFEITOS WOW
// ==========================================================

export function ProfessionalChart({
  // Dados
  data = [],
  type = 'bar', // 'bar', 'line', 'doughnut', 'area', 'comparison'
  title = 'Análise de Dados',
  subtitle = '',
  
  // Estilo
  colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
  gradient = true,
  glassmorphism = true,
  
  // Dimensões
  height = 350,
  width = null,
  
  // Métricas
  goal = null,
  showGrowth = true,
  showStats = true,
  showLegend = true,
  showGrid = true,
  showTooltip = true,
  showAnimation = true,
  showExport = true,
  showFullscreen = true,
  showDateRange = false,
  
  // Formatação
  currency = 'BRL',
  percentage = false,
  decimals = 0,
  
  // Interatividade
  onSegmentClick = null,
  onRefresh = null,
  
  // Estados
  loading = false,
  error = null,
  emptyMessage = 'Nenhum dado disponível',
  
  // Classe CSS
  className = '',
  
  // Dados comparativos (para gráfico de comparação)
  comparisonData = null,
  comparisonLabel = 'Período anterior'
}) {
  
  const isMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  // ==========================================================
  // 📈 FORMATAR VALORES COM ESTILO
  // ==========================================================
  const formatValue = (value, forceDecimals = decimals) => {
    if (value === null || value === undefined) return '—';
    
    if (currency) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        minimumFractionDigits: forceDecimals,
        maximumFractionDigits: forceDecimals
      }).format(value);
    }
    
    if (percentage) {
      return `${value.toFixed(forceDecimals)}%`;
    }
    
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: forceDecimals,
      maximumFractionDigits: forceDecimals
    });
  };

  // ==========================================================
  // 📊 CALCULAR ESTATÍSTICAS WOW
  // ==========================================================
  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;

    const values = data.map(item => item.value || item.total || 0).filter(v => !isNaN(v));
    
    if (values.length === 0) return null;

    const total = values.reduce((a, b) => a + b, 0);
    const avg = total / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const last = values[values.length - 1];
    const first = values[0];
    const previous = values[values.length - 2] || last;

    // Crescimento
    const growth = previous > 0 ? ((last - previous) / previous) * 100 : 0;
    
    // Tendência
    let trend = 'stable';
    if (growth > 5) trend = 'up';
    else if (growth < -5) trend = 'down';
    
    // Volatilidade (variação entre valores)
    const volatility = values.reduce((acc, val, i, arr) => {
      if (i === 0) return acc;
      return acc + Math.abs((val - arr[i-1]) / arr[i-1]);
    }, 0) / (values.length - 1) * 100;

    // Projeção (simples)
    const projection = last * (1 + (growth / 100));

    return {
      total,
      avg,
      max,
      min,
      last,
      first,
      growth,
      trend,
      volatility: isNaN(volatility) ? 0 : volatility,
      projection,
      percentage: total > 0 ? (last / total) * 100 : 0
    };
  }, [data]);

  // ==========================================================
  // 🎨 CORES COM GRADIENTE (EFEITO WOW)
  // ==========================================================
  const getGradient = (ctx, color) => {
    if (!gradient || !ctx) return color;
    
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.6, color + 'dd');
    gradient.addColorStop(1, color + '80');
    return gradient;
  };

  // ==========================================================
  // 📊 PREPARAR DADOS DO GRÁFICO
  // ==========================================================
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const labels = data.map(item => item.month || item.label || item.name || '');
    const values = data.map(item => item.value || item.total || 0);

    // Para gráficos de pizza/donut
    if (type === 'doughnut') {
      return {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: 'white',
          borderWidth: 3,
          hoverOffset: 20,
          hoverBorderColor: 'white',
          hoverBorderWidth: 4,
        }]
      };
    }

    // Dataset principal
    const datasets = [{
      label: title,
      data: values,
      backgroundColor: Array.isArray(colors) ? colors[0] : colors,
      borderColor: Array.isArray(colors) ? colors[0] : colors,
      borderWidth: 2,
      borderRadius: 8,
      barPercentage: isMobile ? 0.6 : 0.7,
      categoryPercentage: isMobile ? 0.7 : 0.8,
      tension: 0.4,
      fill: type === 'area',
      pointRadius: isMobile ? 3 : 4,
      pointHoverRadius: isMobile ? 6 : 8,
      pointBackgroundColor: 'white',
      pointBorderColor: Array.isArray(colors) ? colors[0] : colors,
      pointBorderWidth: 2,
    }];

    // Dataset comparativo (se existir)
    if (comparisonData && type !== 'doughnut') {
      datasets.push({
        label: comparisonLabel,
        data: comparisonData.map(item => item.value || item.total || 0),
        backgroundColor: colors[1] + '80',
        borderColor: colors[1],
        borderWidth: 2,
        borderDash: [5, 5],
        borderRadius: 8,
        barPercentage: isMobile ? 0.6 : 0.7,
        categoryPercentage: isMobile ? 0.7 : 0.8,
        tension: 0.4,
        fill: false,
        pointRadius: isMobile ? 2 : 3,
        pointHoverRadius: isMobile ? 5 : 6,
      });
    }

    return { labels, datasets };
  }, [data, type, colors, isMobile, title, comparisonData, comparisonLabel]);

  // ==========================================================
  // ⚙️ CONFIGURAÇÕES AVANÇADAS
  // ==========================================================
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: showAnimation ? {
      duration: 1200,
      easing: 'easeOutQuart',
      delay: (context) => context.dataIndex * 50
    } : false,
    
    plugins: {
      legend: {
        display: showLegend && type !== 'doughnut',
        position: isMobile ? 'bottom' : 'top',
        align: 'center',
        labels: {
          boxWidth: isMobile ? 10 : 14,
          padding: isMobile ? 8 : 12,
          font: {
            size: isMobile ? 10 : 12,
            weight: '500',
            family: 'Inter, system-ui, sans-serif'
          },
          color: '#334155',
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      
      tooltip: {
        enabled: showTooltip && showTooltips,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f1f5f9',
        titleFont: {
          size: isMobile ? 11 : 13,
          weight: '600',
          family: 'Inter, system-ui, sans-serif'
        },
        bodyColor: '#cbd5e1',
        bodyFont: {
          size: isMobile ? 10 : 12,
          family: 'Inter, system-ui, sans-serif'
        },
        borderColor: '#334155',
        borderWidth: 1,
        padding: isMobile ? 8 : 12,
        cornerRadius: 8,
        caretSize: 8,
        boxPadding: 4,
        usePointStyle: true,
        
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== undefined) {
              label += formatValue(context.parsed.y);
            } else if (context.parsed !== undefined) {
              label += formatValue(context.parsed);
            }
            return label;
          },
          
          afterBody: (context) => {
            if (stats && context[0]?.dataIndex !== undefined) {
              const value = context[0].raw;
              const percent = ((value / stats.total) * 100).toFixed(1);
              return [`📊 ${percent}% do total`];
            }
            return [];
          }
        }
      },
      
      // Linha da meta (efeito especial)
      annotation: goal ? {
        annotations: {
          goalLine: {
            type: 'line',
            yMin: goal,
            yMax: goal,
            borderColor: '#ef4444',
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              content: `🎯 Meta: ${formatValue(goal)}`,
              enabled: true,
              position: 'end',
              backgroundColor: '#ef4444',
              color: 'white',
              font: { size: 10, weight: 'bold' },
              padding: 4,
              borderRadius: 4
            }
          }
        }
      } : null
    },
    
    scales: type !== 'doughnut' ? {
      y: {
        beginAtZero: true,
        grid: {
          color: showGrid ? '#e2e8f0' : 'transparent',
          drawBorder: false,
          lineWidth: 0.5
        },
        ticks: {
          callback: (value) => formatValue(value, 0),
          font: { size: isMobile ? 9 : 11 },
          color: '#64748b',
          maxTicksLimit: isMobile ? 5 : 8
        },
        title: {
          display: false
        }
      },
      x: {
        grid: { display: false },
        ticks: {
          font: { size: isMobile ? 9 : 11 },
          color: '#64748b',
          maxRotation: isMobile ? 30 : 0,
          maxTicksLimit: isMobile ? 6 : 12
        }
      }
    } : {}
  };

  // ==========================================================
  // 🎯 ÍCONE DE TENDÊNCIA
  // ==========================================================
  const TrendIcon = () => {
    if (!stats) return null;
    
    if (stats.trend === 'up') {
      return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    }
    if (stats.trend === 'down') {
      return <TrendingDown className="w-4 h-4 text-rose-500" />;
    }
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  // ==========================================================
  // 🖼️ RENDER - VERSÃO ULTRA ÉLITE
  // ==========================================================
  
  // Loading state com efeito shimmer
  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`relative overflow-hidden bg-white rounded-2xl shadow-xl border border-slate-100 ${className}`}
        ref={containerRef}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-100 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
        <div className={`p-${isMobile ? '4' : '6'}`}>
          <div className="h-6 w-48 bg-slate-200 rounded-lg mb-4" />
          <div className={`h-${isMobile ? '40' : '64'} bg-slate-100 rounded-xl`} />
        </div>
      </motion.div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl border border-rose-100 p-8 text-center"
      >
        <div className="text-rose-400 text-4xl mb-3">⚠️</div>
        <p className="text-rose-600 font-medium">{error}</p>
      </motion.div>
    );
  }

  // Empty state
  if (!chartData || data.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl border border-slate-100 p-12 text-center"
      >
        <div className="text-slate-300 text-5xl mb-4">📊</div>
        <p className="text-slate-400 text-lg">{emptyMessage}</p>
      </motion.div>
    );
  }

  const ChartComponent = 
    type === 'line' || type === 'area' ? Line :
    type === 'doughnut' ? Doughnut : Bar;

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`
        relative 
        ${glassmorphism ? 'bg-white/90 backdrop-blur-xl' : 'bg-white'} 
        rounded-2xl 
        shadow-2xl 
        border 
        ${glassmorphism ? 'border-white/20' : 'border-slate-100'}
        overflow-hidden
        ${isFullscreen ? 'fixed inset-4 z-50' : ''}
        ${className}
      `}
      style={{
        boxShadow: glassmorphism ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : undefined
      }}
    >
      {/* Efeito de brilho no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />

      {/* Header com gradiente */}
      <div className={`
        relative
        bg-gradient-to-r 
        from-slate-50/50 
        via-white 
        to-slate-50/50 
        border-b 
        ${glassmorphism ? 'border-white/20' : 'border-slate-100'}
        px-${isMobile ? '4' : '6'}
        py-${isMobile ? '3' : '4'}
      `}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Ícone animado */}
            <motion.div
              whileHover={{ rotate: 180, scale: 1.1 }}
              transition={{ duration: 0.3 }}
              className={`
                p-${isMobile ? '2' : '2.5'} 
                rounded-xl 
                bg-gradient-to-br 
                from-blue-500/10 
                to-indigo-500/10
                border
                border-blue-500/20
              `}
            >
              {type === 'bar' && <BarChart3 className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600`} />}
              {type === 'line' && <LineChart className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-indigo-600`} />}
              {type === 'doughnut' && <PieChart className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600`} />}
            </motion.div>
            
            <div>
              <h3 className={`font-bold text-slate-900 flex items-center gap-2 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {title}
                {stats && showGrowth && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`
                      inline-flex items-center gap-1 
                      px-2 py-0.5 
                      rounded-full 
                      text-xs 
                      font-medium
                      ${stats.trend === 'up' ? 'bg-emerald-100 text-emerald-700' : ''}
                      ${stats.trend === 'down' ? 'bg-rose-100 text-rose-700' : ''}
                      ${stats.trend === 'stable' ? 'bg-slate-100 text-slate-700' : ''}
                    `}
                  >
                    <TrendIcon />
                    {stats.growth > 0 ? '+' : ''}{stats.growth.toFixed(1)}%
                  </motion.span>
                )}
              </h3>
              {subtitle && (
                <p className={`text-slate-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>{subtitle}</p>
              )}
            </div>
          </div>

          {/* Ações rápidas */}
          <div className="flex items-center gap-1">
            {showTooltip && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowTooltips(!showTooltips)}
                className={`
                  p-${isMobile ? '1.5' : '2'} 
                  rounded-lg 
                  hover:bg-slate-100 
                  transition-colors
                  relative
                  group
                `}
                title={showTooltips ? 'Ocultar tooltips' : 'Mostrar tooltips'}
              >
                {showTooltips ? 
                  <Eye className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} /> : 
                  <EyeOff className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
                }
              </motion.button>
            )}

            {onRefresh && (
              <motion.button
                whileHover={{ rotate: 180 }}
                whileTap={{ scale: 0.95 }}
                onClick={onRefresh}
                className={`p-${isMobile ? '1.5' : '2'} rounded-lg hover:bg-slate-100 transition-colors`}
                title="Atualizar dados"
              >
                <RefreshCw className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
              </motion.button>
            )}

            {showExport && !isMobile && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`p-${isMobile ? '1.5' : '2'} rounded-lg hover:bg-slate-100 transition-colors`}
                title="Exportar dados"
              >
                <Download className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
              </motion.button>
            )}

            {showFullscreen && !isMobile && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={`p-${isMobile ? '1.5' : '2'} rounded-lg hover:bg-slate-100 transition-colors`}
                title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              >
                {isFullscreen ? 
                  <Minimize2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} /> : 
                  <Maximize2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
                }
              </motion.button>
            )}
          </div>
        </div>

        {/* Stats rápidas */}
        {stats && showStats && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`
              grid 
              ${isMobile ? 'grid-cols-3 gap-1 mt-3' : 'grid-cols-4 gap-4 mt-4'}
            `}
          >
            <div className={`${isMobile ? 'text-center' : ''}`}>
              <div className={`text-${isMobile ? 'xs' : 'sm'} text-slate-500 flex items-center gap-1 ${isMobile ? 'justify-center' : ''}`}>
                <Target className="w-3 h-3" /> Total
              </div>
              <div className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {formatValue(stats.total, 0)}
              </div>
            </div>
            
            <div className={`${isMobile ? 'text-center' : ''}`}>
              <div className={`text-${isMobile ? 'xs' : 'sm'} text-slate-500 flex items-center gap-1 ${isMobile ? 'justify-center' : ''}`}>
                <Award className="w-3 h-3" /> Média
              </div>
              <div className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {formatValue(stats.avg, 0)}
              </div>
            </div>
            
            <div className={`${isMobile ? 'text-center' : ''}`}>
              <div className={`text-${isMobile ? 'xs' : 'sm'} text-slate-500 flex items-center gap-1 ${isMobile ? 'justify-center' : ''}`}>
                <Zap className="w-3 h-3" /> Pico
              </div>
              <div className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {formatValue(stats.max, 0)}
              </div>
            </div>

            {!isMobile && (
              <div>
                <div className="text-sm text-slate-500 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Projeção
                </div>
                <div className="font-bold text-slate-900">
                  {formatValue(stats.projection, 0)}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Gráfico */}
<div 
  className={`
    relative
    ${isFullscreen ? 'h-[calc(100vh-180px)]' : ''}
    p-${isMobile ? '3' : '4'}
  `}
  style={{ height: isFullscreen ? undefined : height }}
>
  <ChartComponent
    ref={chartRef}
    data={chartData}
    options={options}
    onClick={(event, elements) => {
      if (onSegmentClick && elements.length > 0) {
        const index = elements[0].index;
        onSegmentClick(data[index], index);
      }
    }}
    // ✅ onHover removido - problema resolvido!
  />
</div>

      {/* Footer com meta (se existir) */}
      {goal && stats && !isFullscreen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`
            border-t 
            ${glassmorphism ? 'border-white/20' : 'border-slate-100'}
            px-${isMobile ? '4' : '6'}
            py-${isMobile ? '3' : '4'}
            bg-gradient-to-r
            from-slate-50/30
            to-white/30
          `}
        >
          <div className="flex items-center justify-between mb-2">
            <span className={`text-${isMobile ? 'xs' : 'sm'} text-slate-600 flex items-center gap-1`}>
              <Target className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-400`} />
              Meta: {formatValue(goal)}
            </span>
            <span className={`font-bold text-${isMobile ? 'sm' : 'base'} text-blue-600`}>
              {Math.min(100, Math.round((stats.last / goal) * 100))}%
            </span>
          </div>
          <div className="relative w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, (stats.last / goal) * 100)}%` }}
              transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
              style={{
                boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)'
              }}
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default ProfessionalChart;